// src/ConsumerApp/context/ToastContext.js

import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { theme } from '../../assets/theme/theme';

const ToastContext = createContext(null);

// Best-practice default durations (in ms)
const DURATION_BY_TYPE = {
  success: 3500, // Quick confirmation
  info: 4500,    // Informative guidance
  warning: 5000, // Needs attention
  error: 6500,   // Crucial error context
};

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const remainingTimeRef = useRef(0);
  const startTimeRef = useRef(0);

  const clearExistingTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const hideToast = useCallback(() => {
    clearExistingTimer();
    setToast(null);
  }, []);

  const startDismissTimer = useCallback((duration) => {
    clearExistingTimer();
    remainingTimeRef.current = duration;
    startTimeRef.current = Date.now();

    timerRef.current = setTimeout(() => {
      setToast(null);
    }, duration);
  }, []);

  const showToast = useCallback((message, type = 'success', customDuration) => {
    const duration = customDuration ?? DURATION_BY_TYPE[type] ?? 3500;

    setToast({
      message,
      type,
      duration,
      id: Date.now(),
    });

    startDismissTimer(duration);
  }, [startDismissTimer]);

  // Pause timer on hover
  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(1000, remainingTimeRef.current - elapsed);
    }
  };

  // Resume timer on mouse leave
  const handleMouseLeave = () => {
    if (toast) {
      startDismissTimer(remainingTimeRef.current);
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}

      {toast && (
        <div style={toastStyles.overlay}>
          <div
            style={{
              ...toastStyles.toastPill,
              ...(toastStyles.typeBorders[toast.type] || toastStyles.typeBorders.success),
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Type-Specific Status Icon */}
            <div
              style={{
                ...toastStyles.iconBadge,
                ...(toastStyles.typeIconBg[toast.type] || toastStyles.typeIconBg.success),
              }}
            >
              {toast.type === 'error' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
              {toast.type === 'success' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {toast.type === 'warning' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
              {toast.type === 'info' && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              )}
            </div>

            <span style={toastStyles.messageText}>{toast.message}</span>

            <button type="button" style={toastStyles.closeBtn} onClick={hideToast} aria-label="Close">
              ✕
            </button>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const toastStyles = {
  overlay: {
    position: 'fixed',
    top: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999999,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    width: '100%',
    maxWidth: 520,
    padding: '0 16px',
    boxSizing: 'border-box',
  },
  toastPill: {
    pointerEvents: 'auto',
    backgroundColor: '#FFFFFF',
    borderRadius: theme.radius?.[12] || 12,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    maxWidth: '100%',
  },
  typeBorders: {
    success: { borderColor: '#86EFAC' },
    error: { borderColor: '#FCA5A5' },
    warning: { borderColor: '#FDE68A' },
    info: { borderColor: '#BFDBFE' },
  },
  typeIconBg: {
    success: { backgroundColor: '#DCFCE7' },
    error: { backgroundColor: '#FEE2E2' },
    warning: { backgroundColor: '#FEF3C7' },
    info: { backgroundColor: '#EFF6FF' },
  },
  iconBadge: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  messageText: {
    fontSize: 13.5,
    fontFamily: theme.typography?.bodyMD?.fontFamily || 'sans-serif',
    color: '#2B2A29',
    fontWeight: '600',
    lineHeight: '18px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#94A3B8',
    cursor: 'pointer',
    padding: 4,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
};